import type { ApprovalReason } from '@/api/types';
import { APPROVAL_REASON_META, APPROVAL_REASONS_ORDERED } from '@/lib/approval-reasons';
import { cn } from '@/lib/utils';

/** Single-select radio group for the approve-flow confidence basis.
 *
 *  Controlled — the caller owns `value` (null until a reason is chosen) and
 *  reacts via `onChange`. Native radios inside a `fieldset`, so it's
 *  keyboard-navigable (arrow keys move + select, matching the retro's
 *  keyboard-first review ask) with the full tooltip on hover. Used by every
 *  single-finding approve surface; bulk approve records `supplier_sampled`
 *  without this picker. */
export function ApprovalReasonPicker({
  value,
  onChange,
  disabled = false,
  className,
}: {
  value: ApprovalReason | null;
  onChange: (next: ApprovalReason) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <fieldset
      className={cn('grid gap-1.5 rounded-md border border-border bg-secondary/20 p-3', className)}
      disabled={disabled}
    >
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Approval basis (required)
      </legend>
      <div className="grid gap-1.5">
        {APPROVAL_REASONS_ORDERED.map((reason) => {
          const meta = APPROVAL_REASON_META[reason];
          const checked = value === reason;
          return (
            <label
              key={reason}
              title={meta.tooltip}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-sm transition-colors hover:bg-secondary/60',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                type="radio"
                name="approval-reason"
                className="mt-0.5"
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(reason)}
              />
              <span>
                <span className="font-medium">{meta.label}</span>
                <span className="block text-xs text-brand-slate">{meta.tooltip}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
